import { FileText, Loader2, Mic } from "lucide-react";
import { useEffect, useRef } from "react";

import type { TranscriptItem } from "@elation/api-client/src/stitch-web/models";

import { cn } from "@src/shared/lib/utils";
import { formatDurationMmSs } from "@src/shared/utils/dates";

export interface TranscriptViewProps {
  items: TranscriptItem[];
  isRecording?: boolean;
  className?: string;
}

const SCROLL_BOTTOM_THRESHOLD = 50;

export const TranscriptView = ({
  items,
  isRecording = false,
  className,
}: TranscriptViewProps) => {
  const listRef = useRef<HTMLOListElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    isAtBottomRef.current =
      scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps intentionally drive re-scroll when transcript length or recording UI changes; body only reads refs/DOM
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [items.length, isRecording]);

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 overflow-hidden rounded-md",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-1.5">
          <FileText className="size-4 text-slate-500" aria-hidden="true" />
          <h3 className="text-sm font-semibold tracking-tight text-slate-900">
            Transcript
          </h3>
          <span className="text-xs font-medium text-slate-400">
            {items.length}
          </span>
        </div>
        {isRecording ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
            <span
              className="size-1.5 animate-pulse rounded-full bg-red-500"
              aria-hidden="true"
            />
            Recording
          </span>
        ) : null}
      </div>

      {items.length === 0 ? (
        <EmptyState isRecording={isRecording} />
      ) : (
        <ol
          ref={listRef}
          onScroll={handleScroll}
          className="flex max-h-72 flex-col gap-1 overflow-y-auto pr-1"
          aria-label="Transcript items"
        >
          {items.map((item) => (
            <TranscriptRow key={item.id} item={item} />
          ))}
        </ol>
      )}
    </div>
  );
};

function TranscriptRow({ item }: { item: TranscriptItem }) {
  const isPending = item.state === "not_sent" || item.state === "sent";
  const isErrored = item.state === "errored";

  return (
    <li className="group flex flex-col gap-1 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-50">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] tabular-nums text-slate-400">
          {formatDurationMmSs(item.startOffsetMs)}
        </span>
        {isPending ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400"
            title="Awaiting processing"
          >
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
            processing
          </span>
        ) : null}
        {isErrored ? (
          <span
            className="text-[10px] font-medium text-red-600"
            title="Failed to process"
          >
            failed
          </span>
        ) : null}
      </div>
      <p
        className={cn(
          "text-sm leading-snug text-slate-800",
          isPending && "italic text-slate-500",
          isErrored && "text-red-700",
        )}
      >
        {item.text || (
          <span className="text-slate-400">(no transcription)</span>
        )}
      </p>
    </li>
  );
}

function EmptyState({ isRecording }: { isRecording: boolean }) {
  return (
    <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center">
      <Mic className="size-5 text-slate-400" aria-hidden="true" />
      <p className="text-xs text-slate-500">
        {isRecording
          ? "Listening… transcript will appear here as the visit is recorded."
          : "No transcript yet. Start a visit to begin recording."}
      </p>
    </div>
  );
}
